import { FC, Fragment, useContext } from 'react';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import { FocusMarkerType, FocusedMarker, Position } from 'ts/hotel/interfaces/Generic';
import { InfoWindow, Marker } from '@react-google-maps/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faThumbTack } from '@fortawesome/free-solid-svg-icons';
import { MapHotelContext } from 'contexts/MapHotelContext';
import Rating from 'components/ui/Rating/Rating';
import { getCenteredHotel, formatPriceHotel } from 'utils/hotel';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { imgWaypointA, imgWaypointB } from 'constants/generic';

interface CustomMarkerProps {
  hotel?: Hotel;
  setDirections: (directions: any) => void;
  onClickFrom: (from?: Position) => void;
  onClickTo: (to?: Position) => void;
  focusedMarker?: FocusedMarker;
  setFocusedMarker: (focusedMarker: FocusedMarker) => void;
  zIndex: number;
  markerPosition: Position;
  onClickMarker: (type: string, position?: Position) => void;
  icon: { url: string };
  additionalCenter?: Position;
  markerType: FocusMarkerType;
}

const CustomMarker: FC<CustomMarkerProps> = ({ hotel, ...props }) => {
  const { setCentered, setFocusedHotel, currency } = useContext(MapHotelContext);
  const hotelOrCenterPosition = hotel ? getCenteredHotel(hotel) : props.markerPosition;
  const { t } = useTranslation();

  const showMarker = () => {
    return (
      props.focusedMarker?.type === props.markerType &&
      props.focusedMarker?.open &&
      props.markerPosition?.lat === props.focusedMarker.lat &&
      props.markerPosition?.lng === props.focusedMarker.lng
    );
  };

  return (
    <Marker
      zIndex={props.zIndex}
      position={props.markerPosition}
      icon={props.icon}
      onClick={() => props.onClickMarker(props.markerType, getCenteredHotel(hotel))}
    >
      {showMarker() && (
        <InfoWindow
          position={props.markerPosition}
          onCloseClick={() => props.setFocusedMarker({ ...props.focusedMarker!, open: false })}
        >
          <div className="map-marker">
            {!!hotel && (
              <>
                <div className="text-bold">{hotel.item?.name}</div>
                <Rating value={hotel.item?.star || 0} className="mb-5" />
                <div className="mb-5">{hotel.addressInfo?.address}</div>
                <div className="mb-5 text-bold text-lg">
                  {formatPriceHotel(hotel.roomClasses[0].price, currency)}
                </div>
              </>
            )}
            {!!props.markerPosition && (
              <div className="text-bold mb-10">{props.markerPosition.address}</div>
            )}
            <div className="marker-actions">
              <button
                className="btn btn-default"
                onClick={() => props.onClickFrom(hotelOrCenterPosition)}
              >
                <img src={imgWaypointA} alt="" /> {t('group.from')}
              </button>
              <button
                className="btn btn-default"
                onClick={() => props.onClickTo(hotelOrCenterPosition)}
              >
                <img src={imgWaypointB} alt="" />
                {t('group.to')}
              </button>
              <button
                className="btn btn-default"
                onClick={() => setCentered(hotelOrCenterPosition)}
              >
                <FontAwesomeIcon icon={faArrowRight} />
                {t('hotel.center_me')}
              </button>
              {!!hotel && (
                <button
                  className="btn btn-default"
                  onClick={() =>
                    setFocusedHotel({
                      lat: hotel?.position?.latitude || 0,
                      lng: hotel?.position?.longitude || 0,
                      htuKey: hotel?.htuKey,
                      type: 'hotel',
                      open: true,
                      name: '',
                    })
                  }
                >
                  <FontAwesomeIcon icon={faThumbTack} />
                  {t('hotel.focus')}
                </button>
              )}
            </div>
          </div>
        </InfoWindow>
      )}
    </Marker>
  );
};

export default withErrorBoundary(CustomMarker, 'CustomMarker');
